import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HiOutlineSearch, HiOutlineSparkles, HiOutlineMenu, HiOutlineChatAlt2 } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import NotificationBell from './NotificationBell';
import api from '../../utils/api';

const typeConfig = {
  customer:  { label: 'Customer', color: 'bg-blue-100 text-blue-700', path: '/customers', searchCode: 'customer_code' },
  supplier:  { label: 'Supplier', color: 'bg-purple-100 text-purple-700', path: '/suppliers', searchCode: 'supplier_code' },
  item:      { label: 'Item',     color: 'bg-amber-100 text-amber-700', path: '/inventory/items', searchCode: 'item_code' },
  account:   { label: 'Account',  color: 'bg-emerald-100 text-emerald-700', path: '/finance/chart-of-accounts', searchCode: 'account_code' },
  invoice:   { label: 'Invoice',  color: 'bg-red-100 text-red-700', path: '/orders/invoices' },
};

const Header = ({ onToggleAI, onToggleFeedback, isSidebarCollapsed = false, onOpenMobileNav }) => {
  const { user, logout, isAiEnabled, isFeedbackEnabled } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const profileMenuRef = useRef(null);
  const timerRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); setShowResults(false); return; }
    setLoading(true);
    try {
      const res = await api.get('/search', { params: { q } });
      setResults(res.data);
      setShowResults(true);
    } catch { setResults([]); } finally { setLoading(false); }
  }, []);

  const onInput = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(val), 300);
  };

  const pickResult = (r) => {
    const cfg = typeConfig[r.type];
    if (cfg) {
      if (r.type === 'invoice') {
        navigate(`${cfg.path}/${r.id}`);
      } else {
        const code = r[cfg.searchCode];
        navigate(code ? `${cfg.path}?search=${encodeURIComponent(code)}` : cfg.path);
      }
    }
    setShowResults(false);
    setSearchQuery('');
  };

  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className={`h-16 bg-white border-b border-gray-200 flex items-center gap-3 justify-between flex-shrink-0 transition-all duration-300 dark:bg-slate-900 dark:border-slate-800 ${isSidebarCollapsed ? 'px-4 lg:px-8 xl:px-10' : 'px-4 lg:px-6'}`}>
      {/* Mobile nav toggle — hidden on desktop where the sidebar is always visible */}
      <button
        type="button"
        onClick={onOpenMobileNav}
        aria-label="Open navigation menu"
        className="lg:hidden flex-shrink-0 p-2 -ml-1 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <HiOutlineMenu className="w-5 h-5" />
      </button>

      {/* Search */}
      <div className={`flex-1 relative transition-all duration-300 ${isSidebarCollapsed ? 'max-w-2xl' : 'max-w-lg'}`} ref={searchRef}>
        <div className="relative">
          <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={onInput}
            onFocus={() => results.length > 0 && setShowResults(true)}
            placeholder="Search transactions, customers, items..."
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder:text-gray-400 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-400"
          />
          {loading && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />}
        </div>
        {showResults && (
          <div className="absolute top-full mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto dark:bg-slate-900 dark:border-slate-700">
            {results.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-slate-400">No results found</div>
            ) : (
              results.map((r, i) => {
                const cfg = typeConfig[r.type] || {};
                return (
                  <button key={`${r.type}-${r.id}-${i}`} onClick={() => pickResult(r)}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-0 transition-colors dark:hover:bg-slate-800 dark:border-slate-800">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color || ''}`}>{cfg.label}</span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate block dark:text-slate-100">{r.name || r.invoice_number}</span>
                      <span className="text-xs text-gray-500 dark:text-slate-400">{r.customer_code || r.supplier_code || r.item_code || r.account_code || r.invoice_number || ''}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* Give Feedback — visibility comes from JourneyPlatformDB, so it can be
            switched off for every tenant with one UPDATE and no deploy.
            Deliberately a quiet outline pill rather than a second gradient: two
            competing gradients side by side would dilute Ask Q AI, which is the
            headline feature. It sits to the LEFT because it is the secondary
            action — DOM order in this flex row is what places it there. */}
        {isFeedbackEnabled && (
          <button
            type="button"
            onClick={onToggleFeedback}
            title="Give feedback"
            aria-label="Give feedback"
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100 dark:focus:ring-offset-slate-900"
          >
            <HiOutlineChatAlt2 className="w-4 h-4" />
            {/* md, not sm like the AI button: two labelled pills plus the search
                box and the bell overflow a small tablet header. */}
            <span className="hidden md:inline">Feedback</span>
          </button>
        )}

        {/* AI Chat Button — hidden when AI is disabled system-wide */}
        {isAiEnabled && (
          <button
            onClick={onToggleAI}
            className="relative flex items-center gap-2 px-3 py-2 text-white text-sm rounded-lg bg-gradient-to-r from-fuchsia-500 via-violet-500 to-indigo-500 hover:from-fuchsia-600 hover:via-violet-600 hover:to-indigo-600 transition-all shadow-md shadow-fuchsia-500/30 border border-white/20 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1 dark:focus:ring-offset-slate-900"
          >
            <HiOutlineSparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Ask Q AI</span>
          </button>
        )}

        {/* Notifications */}
        <NotificationBell />

        {/* User Avatar */}
        <div className="relative" ref={profileMenuRef}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-2 hover:bg-gray-100 rounded-lg p-1.5 transition-colors dark:hover:bg-slate-800"
          >
            <div className="w-8 h-8 rounded-full bg-navy-800 text-white flex items-center justify-center text-xs font-bold">
              {user?.full_name?.charAt(0) || 'A'}
            </div>
            <span className="text-sm font-medium text-gray-700 hidden sm:inline dark:text-slate-200">
              {user?.full_name || 'Admin'}
            </span>
          </button>

          {showProfileMenu && (
            <div className="absolute right-0 top-12 w-60 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 dark:bg-slate-900 dark:border-slate-700">
              <div className="px-4 py-2 border-b border-gray-100 dark:border-slate-800">
                <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{user?.full_name}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">{user?.email}</p>
              </div>
              <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-800">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-2">Theme</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setTheme('light'); setShowProfileMenu(false); }}
                    className={`rounded-md px-2.5 py-1.5 text-xs font-medium border transition-colors ${theme === 'light' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-800'}`}
                  >
                    Light Mode
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTheme('dark'); setShowProfileMenu(false); }}
                    className={`rounded-md px-2.5 py-1.5 text-xs font-medium border transition-colors ${theme === 'dark' ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-800'}`}
                  >
                    Dark Mode
                  </button>
                </div>
              </div>
              <button
                onClick={() => {
                  navigate('/change-password');
                  setShowProfileMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Change Password
              </button>
              <button
                onClick={logout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors dark:hover:bg-red-950/40"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
